import React from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import AppButton from './CustomButton';
import { Box, List, ListItem, Collapse, Tooltip, IconButton, Link, } from "@mui/material";
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { styled } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useDispatch } from 'react-redux';
import { logout } from '@/stores/authSlice';
import { AuthService } from '@/services';
import { SidebarItem } from '@/types';
import { getAdminDialerUrl } from '@/utils/dialer';

// Styled components for the Sidebar.
// Three fixed zones: logo + toggle on top, scrollable menu in the middle,
// logout pinned at the bottom — only the menu list scrolls.
const SidebarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '248px',
  backgroundColor: '#DEECFF',
  boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
  maxHeight: '100%',
  overflow: 'hidden',
  transition: 'width 300ms',
  // Collapsed rail: icons only, labels/arrows/logo hidden, logout shrinks to icon.
  '&.smallSidebar': {
    width: 74,
    '& .MuiListItemButton-root': {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      justifyContent: 'center',
    },
    '& .MuiListItemIcon-root': { marginRight: 0 },
    '& .MuiListItemText-root': { display: 'none' },
    '& .expandArrow': { display: 'none' },
    '& .sidebarLogo': { justifyContent: 'center', padding: theme.spacing(1, 1.5) },
    '& .sidebarLogo .logo': { display: 'none' },
    '& .logBtn .MuiButtonBase-root': {
      fontSize: 0,
      minWidth: 1,
      padding: theme.spacing(1.25),
      '& .MuiButton-icon': { margin: 0 },
    },
    '& + .MuiBox-root': { maxWidth: 'calc(100% - 74px)' },
  },
}));

// Fixed row on top holding the logo + collapse toggle.
// White with the same bottom border as the header, so the logo strip reads as
// one continuous bar with the header beside it.
const SidebarHead = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  flexShrink: 0,
  minHeight: 74,
  padding: theme.spacing(1, 2.5),
  gap: theme.spacing(0.5),
  backgroundColor: '#fff',
  borderBottom: '1px solid #DADADA',
  '& .logo': {
    display: 'inline-flex',
    cursor: 'pointer',
    '& img': { maxWidth: '100%' },
  },
}));

// The only scrollable zone, with a slim scrollbar.
const SidebarMenuArea = styled(Box)(() => ({
  flex: 1,
  minHeight: 0,
  width: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'thin',
  scrollbarColor: '#B9CBEC transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#B9CBEC', borderRadius: 8 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9DB6E6' },
}));

const SidebarList = styled(List)(({ }) => ({
  padding: 0,
  width: '100%',
}));

const SidebarListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(2.25, 2.5),
  color: '#5A5A5A',
  transition: '500ms',
  position: 'relative',
  '&:hover': {
    backgroundColor: '#F5F9FF',
    '& .MuiListItemIcon-root': {
      filter: 'inherit',
      opacity: 1,
    },
    '& .MuiListItemText-primary': {
      color: '#002253',
      fontWeight: 500,
    },
  },
  '&.selected': {
    backgroundColor: '#F5F9FF',
    // Left accent so the active item reads clearly against the light rail.
    boxShadow: 'inset 3px 0 0 #002F73',
    '& .MuiListItemIcon-root': {
      filter: 'inherit',
      opacity: 1,
    },
    '& .MuiListItemText-primary': {
      color: '#002253',
      fontWeight: 500,
    },
  },
}));

const SidebarListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  width: 24,
  minWidth: 24,
  marginRight: theme.spacing(1.25),
  filter: 'grayscale(1)',
  opacity: 0.6,
  transition: '500ms',
}));

const SidebarListItemText = styled(ListItemText)(({ }) => ({
  margin: 0,
  // padding: 0,
  // '& .MuiListItemText-primary': {
  //   fontSize: 17,
  //   lineHeight: '23px',
  //   fontWeight: 400,
  //   transition: '500ms',
  // },
}));

const NavLogoutBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  flexShrink: 0,
  padding: theme.spacing(2),
  marginTop: 'auto',
  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
}));


const menuItems: SidebarItem[] = [
  { label: "Dashboard", icon: "/images/dashboard-icon.svg", path: "/dashboard" },
  {
    label: "Subscription",
    icon: "/images/subscription-icon.svg",
    children: [
      { label: "Plans", icon: "/images/subscription-icon.svg", path: "/subscription" },
      { label: "Active Subscriptions", icon: "/images/subscription-icon.svg", path: "/active-subscriptions" },
    ],
  },
  { label: "System Admins", icon: "/images/tenants-icon.svg", path: "/tenants" },
  { label: "Retention Management", icon: "/images/mage-archive.svg", path: "/retention-policies" },
  // {
  //   label: "Work Force",
  //   icon: "/images/work-force-icon.svg",
  //   children: [
  //     { label: "Work Force 1", icon: "/images/work-force-icon.svg", path: "/workforce/1" },
  //     { label: "Work Force 2", icon: "/images/work-force-icon.svg", path: "/workforce/2" },
  //   ]
  // },
  {
    label: "Settings",
    icon: "/images/settings-new-icon.svg",
    children: [
      { label: "Dialer", icon: "/images/dialer-icon.svg", path: getAdminDialerUrl(), target: "_blank" },
      { label: "Vici Dialer Process", icon: "/images/call-icon.svg", path: "/settings/vici-dialer-process" },
      { label: "Whatsapp Setup", icon: "/images/whatsapp-icon.svg", path: "/settings/whatsapp" },
      { label: "Whatsapp Templates", icon: "/images/whatsapp-icon.svg", path: "/settings/whatsapp/template" },
      { label: "Facebook Setup", icon: "/images/facebook-icon.svg", path: "/settings/facebook" },
    ]
  },
  // { label: "AI Assistant", icon: "/images/ai-assistant-icon.svg", path: "/ai-assistant" },
  // { label: "Reviews", icon: "/images/reviews-icon.svg", path: "/reviews" },
  // { label: "Training", icon: "/images/training-icon.svg", path: "/training" },
  // { label: "Escalation", icon: "/images/escalation-icon.svg", path: "/escalation" },
]


const AdminSideBar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState('');
  const [collapsed, setCollapsed] = React.useState(false);

  const sidebarToggle = () => {
    setCollapsed(prev => !prev);
    setOpen('');
  };

  const handleToggle = (label: string) => {
    if (collapsed) {
      setCollapsed(false);
      setOpen(label);
      return;
    }
    setOpen(prev => label === prev ? '' : label);
  };


  // Function to check active status
  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const handleLogout = () => {
    AuthService.logout({ token: localStorage.getItem('fcmToken') })
    dispatch(logout());
    navigate("/login");
  }



  return (
    <SidebarContainer className={collapsed ? 'smallSidebar' : ''}>
      <SidebarHead className="sidebarLogo">
        <Link className="logo" onClick={() => navigate('/')}>
          <img src="/images/logo-2x.png" alt="Logo" />
        </Link>
        <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'} placement="right" arrow>
          <IconButton onClick={sidebarToggle} size="small" aria-label="Toggle sidebar" sx={{ ml: 'auto', mt: 3.5, mr: -2.75 }}>
            {collapsed ? <MenuIcon sx={{ color: '#5A5A5A' }} /> : <MenuOpenIcon sx={{ color: '#5A5A5A' }} />}
          </IconButton>
        </Tooltip>
      </SidebarHead>
      <SidebarMenuArea as="nav" aria-label="main mailbox folders" className="dashNavbar">
        <SidebarList>
          {menuItems.map((item, index) => {
            const hasChildren = Number(item.children?.length) > 0;
            const parentButton = (
              <SidebarListItemButton
                onClick={() => {
                  if (hasChildren) handleToggle(item.label);
                  else if (item.path) navigate(item.path);
                }}
                className={isActive(item.path) ? "selected" : ""}
              >
                <SidebarListItemIcon> <img src={item.icon} alt={item.label} /> </SidebarListItemIcon>
                <SidebarListItemText primary={item.label} />
                {hasChildren && (open === item.label ? <ExpandLess className="expandArrow" /> : <ExpandMore className="expandArrow" />)}
              </SidebarListItemButton>
            );

            return (
              <React.Fragment key={index}>
                <ListItem disablePadding sx={{ mt: 1, ...(hasChildren ? { flexWrap: 'wrap' } : {}) }}>
                  {collapsed ? (
                    <Tooltip title={item.label} placement="right" arrow>
                      <Box sx={{ width: '100%' }}>{parentButton}</Box>
                    </Tooltip>
                  ) : (
                    parentButton
                  )}
                </ListItem>
                {hasChildren && (
                  <Collapse in={open === item.label} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                    <SidebarList disablePadding>
                      {item.children?.map((child, idx) => (
                        <ListItem disablePadding sx={{ mt: 1 }} key={idx}>
                          <SidebarListItemButton
                            sx={{ pl: 6, pr: 2.5, py: 1.5 }}
                            onClick={() => {
                              if (child?.target) window.open(child?.path, child?.target);
                              else if (child?.path) navigate(child?.path)
                            }}
                            className={isActive(child?.path) ? "selected" : ""}
                          >
                            <SidebarListItemIcon>
                              <img src={child.icon} alt={child.label} />
                            </SidebarListItemIcon>
                            <SidebarListItemText primary={child.label} />
                          </SidebarListItemButton>
                        </ListItem>
                      ))}
                    </SidebarList>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </SidebarList>
      </SidebarMenuArea>

      <NavLogoutBox className="logBtn">
        <AppButton
          onClick={() => handleLogout()}
          disableElevation
          variant="outlined"
          size="small"
          startIcon={<img src="/images/logout.svg" alt="Logout" />}
        >
          Logout
        </AppButton>
      </NavLogoutBox>
    </SidebarContainer>
  );
};

export default AdminSideBar;
